import {Form, Formik} from 'formik';
import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {SpanIcon} from '@mazraeapp/react_icon_kit';
import Button from '../../../../../components/button';
import DotsLoading from '../../../../../components/dotsLoading';
import FormikLikeInput from '../../../../../components/formikFileds/likeInput';
import FormikTextArea from '../../../../../components/formikFileds/textArea';
import ToggleSwitch from '../../../../../components/mazraeKit/toggleSwitch';
import {useAuth} from '../../../../../context/authContext';
import useGetAddressByLatLngSearch from '../../../../../services/hookService/getAddressByLatLngSearch/useGetAddressByLatLngSearch';
import useGetStoreRedesignView from '../../../../../services/hookService/getStoreRedesignView/useGetStoreRedesignView';
import useGetStoreSupplierProfile from '../../../../../services/hookService/getStoreSupplierProfile/useGetStoreSupplierProfile';
import {RootState} from '../../../../../store/redux';
import {addAddress} from '../../../../../store/redux/myStoreSelectedLocation';
import {addRegisterStoreFormData} from '../../../../../store/redux/registerStore';
import {getLatLng} from '../../../../../utilities/helper/getLatLng';
import useGetProfile from '../../../../profile/hooks/useGetProfile';
import {
  getStoreName,
  storeDetailSchema,
  switchButtonData,
} from '../../contants';
import QRScanner from '../../../../../components/QrCode/QRScanner';

export const StoreDetail = ({
  type,
  mutateFn,
  setCurrentStep,
  isLoadingMutattion,
}: IStoreDetailProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const {state: locationState} = useLocation();
  const dispatch = useDispatch();
  const {supplierProfile} = useAuth();
  const {profile} = useGetProfile(supplierProfile?.guid || '', true);

  const {address, isLoading: isLoadingAddress} = useGetAddressByLatLngSearch(
    profile?.farmer_profile?.latlong?.[1],
    profile?.farmer_profile?.latlong?.[0],
  );
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState<string>('');

  const state = useSelector((state: RootState) => state.registerStore);
  const [touched, setTouched] = useState<boolean>(false);

  const selectLocation = useSelector(
    (state: RootState) => state.myStoreSelectedLocation,
  );
  const storeType = state?.storeType?.trim() || params?.storeType;
  const isLendTech = storeType
    ? ['dokan', 'khish', 'nasagh'].includes(storeType)
    : false;

  const onSuccessAfterGetStoreView = (data: IStoreDetail) => {
    if (!state.title) {
      dispatch(
        addRegisterStoreFormData({
          title: data.title,
          about: data.about,
          in_person_possibility: data.in_person_possibility,
          has_delivery: data.has_delivery,
          has_guarantee: data.has_guarantee,
          address: data.address,
          land_line: data.land_line ?? '',
          logo: {file: null, logo: data.logo},
          store_photo: {file: null, store_photo: data.store_photo},
          license: {file: null, license: data.license},
          license_url: data.license_url,
          storeType: getStoreName(data.store_type.code),
        }),
      );
      dispatch(
        addAddress({
          id: Number(data.city.id),
          name: `${data.state.name},${data.city.name}`,
          location: data.location,
          state_id: Number(data.state.id),
        }),
      );
    }
  };

  const {data, isLoading} = useGetStoreSupplierProfile({});

  const {storeData, storeDataIsLoading} = useGetStoreRedesignView({
    store_guid: params.store_guid,
    onSuccessAfterGetStoreView,
  });

  useEffect(() => {
    if (isLoadingAddress || !address?.city?.id || selectLocation?.id) return;
    dispatch(
      addAddress({
        id: address?.city.id,
        location: address?.city.location,
        name: address?.city.name,
        state_id: address?.city.state_id,
      }),
    );
  }, [address]);

  useEffect(() => {
    if (locationState?.scrollToInput)
      document.getElementById(`location-input`)?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
  }, []);

  const handleOpenScanner = async () => {
    try {
      setScannerError('');

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setScannerError('دسترسی به دوربین در این دستگاه پشتیبانی نمی‌شود');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {facingMode: 'environment'},
      });
      stream.getTracks().forEach(track => track.stop());

      setShowScanner(true);
    } catch (err: any) {
      console.error('Camera permission error:', err);
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        setScannerError(
          'لطفاً دسترسی به دوربین را در تنظیمات مرورگر فعال کنید',
        );
      } else if (err.name === 'NotFoundError') {
        setScannerError('دوربین پیدا نشد');
      } else {
        setScannerError('خطا در دسترسی به دوربین');
      }
    }
  };

  if (isLoading || storeDataIsLoading)
    return (
      <div className="flex justify-center items-center h-full">
        <DotsLoading />
      </div>
    );

  return (
    <Formik
      initialValues={{
        logo: state.logo,
        title: state.title,
        about: state.about,
        address: state.address,
        license: state.license,
        land_line: state.land_line,
        store_photo: state.store_photo,
        has_delivery: state.has_delivery,
        has_guarantee: state.has_guarantee,
        in_person_possibility: state.in_person_possibility,
        license_url: state.license_url,
      }}
      enableReinitialize
      validationSchema={storeDetailSchema}
      onSubmit={values => {
        if (state?.license?.file || state?.license?.license) {
          mutateFn({
            ...values,
            logo: state?.logo?.file,
            owner_name: state?.name,
            national_card: state.national_card?.file,
            phone_number: data?.phone,
            license: state?.license?.file,
            license_url: state?.license_url,
            store_type: state.selectedInput?.id,
            store_photo: state?.store_photo?.file,
            ...(params.store_guid && {guid: params.store_guid}),
            has_delivery: values?.has_delivery || false,
            has_guarantee: values?.has_guarantee || false,
            in_person_possibility: values?.in_person_possibility || false,
            location: `POINT (${
              selectLocation.location && getLatLng(selectLocation.location)[0]
            } ${
              selectLocation.location && getLatLng(selectLocation.location)[1]
            })`,
            address: values?.address,
            city: selectLocation.id,
            state: selectLocation.state_id,
          });
        }
      }}
    >
      {({setFieldValue, values, errors, submitCount, handleSubmit}) => {
        useEffect(() => {
          if (submitCount > 0 && Object.keys(errors).length > 0) {
            scrollToFirstError(errors);
          }
        }, [submitCount, errors]);

        return (
          <div className="flex flex-col mt-12">
            {/* QR Scanner Modal */}
            {showScanner && (
              <QRScanner
                onScanSuccess={decodedText => {
                  const isValidUrl = /^https?:\/\/.+/.test(decodedText);
                  if (!isValidUrl) {
                    alert(
                      'QR کد معتبر نیست. لطفاً یک QR کد حاوی لینک معتبر اسکن کنید.',
                    );
                    return;
                  }
                  setShowScanner(false);
                  setFieldValue('license_url', decodedText);
                  dispatch(
                    addRegisterStoreFormData({
                      ...state,
                      ...values,
                      license_url: decodedText,
                    }),
                  );
                }}
                onScanFailure={error => {
                  console.error(error);
                }}
                onClose={() => setShowScanner(false)}
              />
            )}

            <Form className="flex flex-col gap-4">
              {/* ... rest of your form sections ... */}
              <section className="flex flex-col" id="title">
                <p className="mb-2 font-medium color-gray-1 text-md">
                  نام فروشگاه <span className="color-red-3">*</span>
                </p>
                <span className="mb-3 text-sm">
                  نام فروشگاه خود را کوتاه و متناسب انتخاب کنید.
                </span>
                <FormikLikeInput
                  name="title"
                  label="مثلا: کشاورز نوین"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('title', e.target.value);
                  }}
                />
              </section>
              <section id="about">
                <FormikTextArea
                  name="about"
                  placeholder=""
                  description="شرح کسب و کار و هرآنچه که فروشگاه شما را منحصر به فرد می‌کند، بطور خلاصه بنویسید."
                  label="درباره فروشگاه"
                  rows={5}
                  labelClass="!text-md font-medium"
                  required
                />
              </section>
              <section id="features">
                <div className="flex flex-col gap-4 justify-between items-center mt-8 mb-3">
                  {switchButtonData.map(item => (
                    <div className="flex justify-between items-center w-full text-md">
                      <span>{item.title}</span>
                      <ToggleSwitch
                        name={item.name}
                        isToggled={(values as any)[item.name]}
                        onToggle={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(item.name, e.target.checked);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {isLendTech && (
                <section className="text-neutral-600">
                  <span className="block mb-1 font-medium text-md">
                    اسکن کیو آر کد مجوز فروشگاه
                  </span>
                  <span className="text-sm">
                    با اسکن کیو آر کد (QRCode) مجوز فروشگاه، بطور خودکار قرارداد
                    همکاری شما با سامانه مزرعه تنظیم می‌شود و میتوانید آن را
                    امضا کنید.
                  </span>

                  {values?.license_url ? (
                    <div
                      className="flex relative gap-3 justify-end items-end mt-2"
                      onClick={() => {
                        setFieldValue('license_url', '');
                        dispatch(
                          addRegisterStoreFormData({
                            ...state,
                            license_url: '',
                          }),
                        );
                      }}
                    >
                      <i className="text-3xl text-gray-400 icon-qr-code" />
                      <span className="text-white bg-red-500 rounded">
                        <i className="absolute text-red-500 left-0.5 text-md -top-2 icon-nameClose-remove" />{' '}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 mt-2">
                      <Button
                        type="button"
                        size="small"
                        baseStyles="flex flex-1 gap-1 items-center justify-end rounded-lg font-medium transition-colors outline-none text-md min-h-[32px] mr-auto mt-2"
                        variant="outline"
                        onClick={e => {
                          e.stopPropagation();
                          handleOpenScanner();
                        }}
                      >
                        <i className="text-md icon-qr-code" />
                        <span>اسکن</span>
                      </Button>
                      {scannerError && (
                        <span className="text-sm text-red-500">
                          {scannerError}
                        </span>
                      )}
                    </div>
                  )}
                </section>
              )}

              <section className="flex flex-col">
                <span className="mb-3 text-md">تصاویر فروشگاه</span>
                <section className="flex flex-col mb-3">
                  <span className="mb-3 text-md">
                    لوگو فروشگاه را بارگذاری کنید.{' '}
                  </span>

                  {state?.logo?.file || state?.logo?.logo ? (
                    <div className="flex flex-col w-full">
                      <div className="w-full h-[128px] rounded-[8px] border-dashed border-[2px] border-gray-700 relative overflow-hidden">
                        <div
                          className="absolute rounded-[8px] bg-white flex items-center justify-center p-1 text-white left-2 top-2"
                          onClick={() => {
                            dispatch(
                              addRegisterStoreFormData({
                                ...state,
                                ...values,
                                logo: {file: null, logo: ''},
                              }),
                            );
                          }}
                        >
                          <SpanIcon className="text-xl text-red-600 icon-nameDelete" />
                        </div>
                        <img
                          className="w-full h-[128px] object-cover"
                          src={
                            state?.logo?.logo
                              ? state?.logo?.logo
                              : (state?.logo?.file &&
                                  URL.createObjectURL(state?.logo?.file)) ||
                                ''
                          }
                          alt="upload_images"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="logo"
                        className="flex gap-1 items-center justify-center text-md h-32 w-full rounded border-[2px] border-dashed text-gray-700"
                      >
                        <SpanIcon className="icon-nameImage-plus text-xxl" />
                      </label>
                      <input
                        onChange={event => {
                          dispatch(
                            addRegisterStoreFormData({
                              ...state,
                              ...values,
                              logo: {
                                file:
                                  (event?.target?.files &&
                                    event?.target?.files.length > 0 &&
                                    event.target.files[0]) ||
                                  null,
                                logo: '',
                              },
                            }),
                          );
                        }}
                        className="hidden"
                        type="file"
                        id="logo"
                        name="logo"
                        accept="image/png, image/jpeg"
                      />
                    </div>
                  )}
                </section>

                <section className="flex flex-col mb-3">
                  <span className="mb-3 text-md">
                    تصویری از محیط فروشگاه بارگذاری کنید.
                  </span>

                  {state?.store_photo?.file ||
                  state?.store_photo?.store_photo ? (
                    <div className="flex flex-col w-full">
                      <div className="w-full h-[128px] rounded-[8px] border-dashed border-[2px] border-gray-700 relative overflow-hidden">
                        <div
                          className="absolute rounded-[8px] bg-white flex items-center justify-center p-1 text-white left-2 top-2"
                          onClick={() => {
                            dispatch(
                              addRegisterStoreFormData({
                                ...state,
                                ...values,
                                store_photo: {
                                  file: null,
                                  store_photo: '',
                                },
                              }),
                            );
                          }}
                        >
                          <SpanIcon className="text-xl text-red-600 icon-nameDelete" />
                        </div>
                        <img
                          className="w-full h-[128px] object-cover"
                          src={
                            state?.store_photo.store_photo
                              ? state?.store_photo.store_photo
                              : (state?.store_photo.file &&
                                  URL.createObjectURL(
                                    state?.store_photo.file,
                                  )) ||
                                ''
                          }
                          alt="upload_images"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="store_photo"
                        className="flex gap-1 items-center justify-center text-md h-32 w-full rounded border-[2px] border-dashed text-gray-700"
                      >
                        <SpanIcon className="icon-nameImage-plus text-xxl" />
                      </label>
                      <input
                        onChange={event => {
                          dispatch(
                            addRegisterStoreFormData({
                              ...state,
                              ...values,
                              store_photo: {
                                file:
                                  (event?.target?.files &&
                                    event?.target?.files.length > 0 &&
                                    event.target.files[0]) ||
                                  null,
                                store_photo: '',
                              },
                            }),
                          );
                        }}
                        className="hidden"
                        type="file"
                        id="store_photo"
                        name="store_photo"
                        accept="image/png, image/jpeg"
                      />
                    </div>
                  )}
                </section>

                <section className="flex flex-col mb-3" id="license-section">
                  <div className="flex">
                    <span className="mb-3">تصویر مجوز کسب و کار</span>
                    <span className="text-red-700">*</span>
                  </div>
                  <span className="mb-3 text-md">
                    تصویر مجوز کسب و کار فروشگاه خود را بارگذاری کنید.
                  </span>

                  {state?.license?.file || state?.license?.license ? (
                    <div className="flex flex-col w-full">
                      <div className="w-full h-[128px] rounded-[8px] border-dashed border-[2px] border-gray-700 relative overflow-hidden">
                        <div
                          className="absolute rounded-[8px] bg-white flex items-center justify-center p-1 text-white left-2 top-2"
                          onClick={() => {
                            dispatch(
                              addRegisterStoreFormData({
                                ...state,
                                ...values,
                                license: {
                                  file: null,
                                  license: '',
                                },
                              }),
                            );
                          }}
                        >
                          <SpanIcon className="text-xl text-red-600 icon-nameDelete" />
                        </div>
                        <img
                          className="w-full h-[128px] object-cover"
                          src={
                            state?.license.license
                              ? state?.license.license
                              : (state?.license.file &&
                                  URL.createObjectURL(state?.license.file)) ||
                                ''
                          }
                          alt="upload_images"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="license"
                        className="flex gap-1 items-center justify-center text-md h-32 w-full rounded border-[2px] border-dashed text-gray-700"
                      >
                        <SpanIcon className="icon-nameImage-plus text-xxl" />
                      </label>
                      <input
                        onChange={event => {
                          dispatch(
                            addRegisterStoreFormData({
                              ...state,
                              ...values,
                              license: {
                                file:
                                  (event?.target?.files &&
                                    event?.target?.files.length > 0 &&
                                    event.target.files[0]) ||
                                  null,
                                license: '',
                              },
                            }),
                          );
                        }}
                        className="hidden"
                        type="file"
                        id="license"
                        name="license"
                        accept="image/png, image/jpeg"
                      />
                    </div>
                  )}

                  {touched &&
                    !state?.license?.license &&
                    !state?.license?.file && (
                      <span className="pt-3 text-xs text-red-500">
                        تصویر مجوز کسب و کار فروشگاه خود را بارگذاری کنید.
                      </span>
                    )}
                </section>
              </section>

              <section className="flex flex-col gap-3" id="address">
                <span>
                  آدرس فروشگاه
                  <span className="color-red-3" id="location-input">
                    *
                  </span>
                </span>
                <div
                  className="flex gap-2 items-center p-2 w-full h-12 text-gray-700 rounded border"
                  onClick={() => {
                    dispatch(
                      addRegisterStoreFormData({
                        ...state,
                        ...values,
                      }),
                    );
                    storeData?.location
                      ? navigate('/my-store-add-location-by-map', {
                          state: {
                            type: type,
                            store_guid: params?.store_guid,
                          },
                        })
                      : navigate('/my-store-add-location-by-map');
                  }}
                >
                  <i className="icon-nameLocation text-xxl" />
                  <span className="text-md">
                    {selectLocation.name || 'انتخاب محدوده'}
                  </span>
                </div>
                <div className="flex gap-2 items-center p-2 w-full h-12 text-gray-700 rounded border">
                  <i className="icon-nameLocation text-xxl" />
                  <input
                    className="w-full h-full outline-none text-md"
                    placeholder="خیابان و شماره پلاک را وارد کنید"
                    onChange={e => setFieldValue('address', e.target.value)}
                    defaultValue={state.address}
                  />
                </div>
                <span className="text-xs text-red-500">
                  {' '}
                  {touched && !selectLocation.location && !state.address && (
                    <span className="text-red-500">
                      آدرس فروشگاه خود را وارد کنید
                    </span>
                  )}
                </span>
              </section>
              <section className="flex flex-col" id="land_line">
                <span className="mb-3 text-md">شماره تلفن ثابت</span>
                <FormikLikeInput
                  name="land_line"
                  type="number"
                  label="شماره تلفن ثابت"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('land_line', e.target.value);
                  }}
                />
              </section>
            </Form>
            <section className="flex flex-1 gap-2 mt-4 h-12">
              <button
                className="flex flex-2 bg-white text-green-main justify-center items-center rounded border-[1px] border-green-main px-3"
                onClick={() => {
                  dispatch(
                    addRegisterStoreFormData({
                      ...state,
                      ...values,
                    }),
                  );
                  setCurrentStep(2);
                }}
              >
                مرحله قبل
              </button>
              <button
                type="submit"
                className="flex flex-1 justify-center items-center h-12 text-white rounded bg-green-main"
                onClick={() => {
                  setTouched(true);
                  handleSubmit();
                }}
              >
                {isLoadingMutattion ? <DotsLoading /> : 'ثبت فروشگاه'}
              </button>
            </section>
          </div>
        );
      }}
    </Formik>
  );
};

export default StoreDetail;

const scrollToFirstError = (errors: any) => {
  const firstErrorField = Object.keys(errors)[0];

  if (!firstErrorField) return;

  const element =
    document.querySelector(`[name="${firstErrorField}"]`) ||
    document.getElementById(firstErrorField);

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    (element as HTMLElement).focus?.();
  }
};
